import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { FAQ, LoadingPage, Panel, theme, useMediaMin } from '@foradevelopment/fora-ui';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';

import { EmployerMyPagesErrorPage, layoutSizeSettings } from '../../components';
import { ManualPayrollReportingPanelList } from '../panels/ManualPayrollReportingPanelList';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  resetLoadingDeleteDrafts,
  resetPayrollReportingStates,
  setSelectedReportingPeriod,
} from '../../redux/payroll/payrollSlice';
import { getPayReportData, getPayReportDataOld } from '../../redux/payroll/payrollThunks';
import { links } from '../../router/links';
import { ReportPayrollParams } from '../../router/paths';

export const ManualPayrollReportingView = () => {
  const dispatch = useAppDispatch();
  const faGroups = useAppSelector(state => state.employer.employerInfo.faGroups);
  const faqs = useAppSelector(state => state.content.content.faqListReportPayroll);
  const reportingPeriods = useAppSelector(state => state.payroll.payMonthOverviews);
  const loadingPayReportData = useAppSelector(state => state.payroll.loadingPayReportData);
  const loadingDeleteDraft = useAppSelector(state => state.payroll.loadingDeleteDrafts);
  const thunkError = useAppSelector(state => state.error.thunkError);
  const ffManageEmployeeAndEmployment = useAppSelector(
    state => state.foraGlobal.settings.ffManageEmployeeAndEmployment
  );

  const { payMonth } = useParams<ReportPayrollParams>();
  const [pageNotFound, setPageNotFound] = useState<boolean>(false);
  const isDesktop = useMediaMin(theme.breakpoint.lg);

  useEffect(() => {
    if (loadingDeleteDraft === ThunkRequestStatus.Rejected) {
      return function cleanup() {
        // componentWillUnmount, deleteDrafts ThunkRequestStatus
        dispatch(resetLoadingDeleteDrafts());
      };
    }
  }, [dispatch, loadingDeleteDraft]);

  useEffect(() => {
    return function cleanup() {
      dispatch(resetPayrollReportingStates());
    };
  }, [dispatch]);

  useEffect(() => {
    if (reportingPeriods?.length > 0) {
      const selectedReportingPeriod = reportingPeriods.find(p => p.payMonth === payMonth);
      if (selectedReportingPeriod && faGroups) {
        dispatch(setSelectedReportingPeriod({ selectedPayMonthOverview: selectedReportingPeriod }));

        if (ffManageEmployeeAndEmployment) {
          dispatch(getPayReportData({ payMonth: selectedReportingPeriod.payMonth }));
        } else {
          dispatch(getPayReportDataOld({ payMonth: selectedReportingPeriod.payMonth }));
        }
      } else {
        // User has visited a url for a paymonth they can't report for
        setPageNotFound(true);
      }
    }
  }, [dispatch, reportingPeriods, payMonth, faGroups, ffManageEmployeeAndEmployment]);

  if (pageNotFound) {
    return <EmployerMyPagesErrorPage type={404} startPageLinkUrl={links.payroll} />;
  }

  if (loadingPayReportData === ThunkRequestStatus.Fulfilled && loadingDeleteDraft === ThunkRequestStatus.Fulfilled) {
    return (
      <>
        <Panel.List
          paddingLeft={0}
          paddingRight={0}
          marginLeft={layoutSizeSettings.removePadding}
          marginRight={layoutSizeSettings.removePadding}
        >
          <ManualPayrollReportingPanelList />
        </Panel.List>
        {faqs.length > 0 && (
          <FAQ testId="review-confirm-faq" layout={isDesktop ? 'drawer' : 'modal'} questionAnswers={faqs} />
        )}
      </>
    );
  }

  if (loadingPayReportData === ThunkRequestStatus.Rejected || loadingDeleteDraft === ThunkRequestStatus.Rejected) {
    return <EmployerMyPagesErrorPage type={thunkError?.code} technicalError={thunkError?.message} />;
  }

  return <LoadingPage />;
};
